import { useState } from 'react';

import { Button } from '@/components/ui/button/button';

import styles from './JoinClassDialog.module.css';

interface JoinClassDialogProps {
  open: boolean;
  onClose: () => void;
  /** 加入成功后回调（父组件负责刷新列表 + 提示） */
  onJoined: () => void;
  joinClass: (code: string) => Promise<{ name: string }>;
}

const CODE_PATTERN = /^[A-Z0-9]{4,16}$/;

/** 从输入中解析邀请码：支持直接输入或粘贴邀请链接 */
function extractCode(raw: string): string {
  const v = raw.trim();
  const match = v.match(/[?&]code=([^&\s#]+)/i);
  if (match) return match[1].trim().toUpperCase();
  return v.toUpperCase();
}

export function JoinClassDialog({ open, onClose, onJoined, joinClass }: JoinClassDialogProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => {
    setInput('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');
    const code = extractCode(input);
    if (!code) {
      setError('请输入邀请码，或粘贴老师发来的邀请链接');
      return;
    }
    if (!CODE_PATTERN.test(code)) {
      setError('邀请码格式不正确（应为 4-16 位字母或数字，或有效的邀请链接）');
      return;
    }
    setLoading(true);
    try {
      await joinClass(code);
      reset();
      onJoined();
    } catch (err) {
      setError(err instanceof Error ? err.message : '加入失败，请检查邀请码是否正确');
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.title}>加入新班级</h2>
        <p className={styles.subtitle}>
          输入老师提供的<span className={styles.hl}>邀请码</span>，或粘贴邀请链接，即可加入班级查看作业。
        </p>

        <label className={styles.label}>邀请码 / 邀请链接</label>
        <input
          className={styles.input}
          value={input}
          autoFocus
          placeholder="例如 MATH03，或 https://.../join?code=MATH03"
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) handleSubmit();
          }}
        />

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            加入班级
          </Button>
        </div>
      </div>
    </div>
  );
}
